// Catálogo de farmacia interna (2026-09-11) — mismo motor que /admin/
// catalogo (lib/billing.ts), pero alcanzable sin el login admin_users: el
// Director Técnico Q.F./B.F. no tiene esa cuenta. Solo drug/supply — a
// diferencia del panel CRM, acá no hay procedimientos ni paquetes.
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { notify } from '@/components/ui/sonner';
import {
  actualizarCatalogoFarmacia,
  crearCatalogoFarmacia,
  importarCatalogoFarmacia,
  listarCatalogoFarmacia,
  type CatalogoItemFarmacia,
  type ResultadoImportarCatalogo,
} from '@/lib/farmaciaInterna';

function formatoUsd(cents: number): string {
  return (cents / 100).toLocaleString('es-EC', { style: 'currency', currency: 'USD' });
}

function leerArchivoComoBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function CatalogoFarmacia(): JSX.Element {
  const { t } = useTranslation();
  const [items, setItems] = useState<CatalogoItemFarmacia[]>();
  const [kind, setKind] = useState<'drug' | 'supply'>('drug');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [precio, setPrecio] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [editandoId, setEditandoId] = useState<number>();
  const [precioEditado, setPrecioEditado] = useState('');
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);
  const [importando, setImportando] = useState(false);
  const [resultadoImportacion, setResultadoImportacion] = useState<ResultadoImportarCatalogo>();

  async function recargar(): Promise<void> {
    setItems(await listarCatalogoFarmacia());
  }

  useEffect(() => {
    void recargar();
  }, []);

  // Al pasar a editar un precio, el foco va a su campo (antes `autoFocus`, que jsx-a11y desaconseja).
  useEffect(() => {
    if (editandoId !== undefined) document.getElementById('catalogo-precio-editado')?.focus();
  }, [editandoId]);

  async function crear(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setGuardando(true);
    try {
      const amountCents = Math.round(Number(precio.replace(',', '.')) * 100);
      const r = await crearCatalogoFarmacia({ kind, code: code.trim(), name: name.trim(), amountCents });
      if (!r.ok) {
        notify({ color: 'red', message: r.error, autoClose: false });
        return;
      }
      setCode('');
      setName('');
      setPrecio('');
      await recargar();
    } finally {
      setGuardando(false);
    }
  }

  function empezarEdicionPrecio(item: CatalogoItemFarmacia): void {
    setEditandoId(item.id);
    setPrecioEditado((item.amountCents / 100).toFixed(2));
  }

  async function guardarPrecio(id: number): Promise<void> {
    const amountCents = Math.round(Number(precioEditado.replace(',', '.')) * 100);
    if (!Number.isFinite(amountCents) || amountCents < 0) return;
    setGuardandoEdicion(true);
    try {
      const r = await actualizarCatalogoFarmacia(id, { amountCents });
      if (!r.ok) {
        notify({ color: 'red', message: r.error, autoClose: false });
        return;
      }
      notify({ color: 'green', message: t('farmaciaApp.catalogo.precioActualizado') });
      setEditandoId(undefined);
      await recargar();
    } finally {
      setGuardandoEdicion(false);
    }
  }

  async function alternarActivo(item: CatalogoItemFarmacia): Promise<void> {
    const r = await actualizarCatalogoFarmacia(item.id, { active: !item.active });
    if (!r.ok) {
      notify({ color: 'red', message: r.error, autoClose: false });
      return;
    }
    await recargar();
  }

  async function importar(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportando(true);
    setResultadoImportacion(undefined);
    try {
      const fileBase64 = await leerArchivoComoBase64(file);
      const r = await importarCatalogoFarmacia(fileBase64);
      if (!r.ok) {
        notify({ color: 'red', message: r.error, autoClose: false });
        return;
      }
      setResultadoImportacion(r.datos);
      await recargar();
    } finally {
      setImportando(false);
    }
  }

  return (
    <div className="space-y-6">
      <form onSubmit={(e) => void crear(e)} className="grid grid-cols-2 gap-2 rounded-lg border border-border p-4 sm:grid-cols-5">
        <div>
          <Label className="mb-1 block text-xs">{t('farmaciaApp.catalogo.tipo')}</Label>
          <Select value={kind} onValueChange={(v) => setKind(v as 'drug' | 'supply')}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="drug">{t('farmaciaApp.catalogo.tipoFarmaco')}</SelectItem>
              <SelectItem value="supply">{t('farmaciaApp.catalogo.tipoInsumo')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-xs">{t('farmaciaApp.catalogo.codigo')}</Label>
          <Input value={code} onChange={(e) => setCode(e.target.value)} required />
        </div>
        <div className="col-span-2">
          <Label className="mb-1 block text-xs">{t('farmaciaApp.catalogo.nombre')}</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <Label className="mb-1 block text-xs">{t('farmaciaApp.catalogo.precio')}</Label>
          <Input type="number" step="0.01" min="0" value={precio} onChange={(e) => setPrecio(e.target.value)} required />
        </div>
        <div className="col-span-2 sm:col-span-5">
          <Button type="submit" disabled={guardando}>
            {guardando ? t('farmaciaApp.catalogo.guardando') : t('farmaciaApp.catalogo.agregar')}
          </Button>
        </div>
      </form>

      <div className="space-y-2 rounded-lg border border-border p-4">
        <p className="text-sm font-semibold text-hce-ink">{t('farmaciaApp.catalogo.importar.titulo')}</p>
        <p className="text-xs text-hce-muted">{t('farmaciaApp.catalogo.importar.formatos')}</p>
        <p className="text-xs text-hce-muted">{t('farmaciaApp.catalogo.importar.columnas')}</p>
        <ol className="list-inside list-decimal text-xs text-hce-muted">
          <li>{t('farmaciaApp.catalogo.importar.colTipo')}</li>
          <li>{t('farmaciaApp.catalogo.importar.colCodigo')}</li>
          <li>{t('farmaciaApp.catalogo.importar.colNombre')}</li>
          <li>{t('farmaciaApp.catalogo.importar.colPrecio')}</li>
          <li>{t('farmaciaApp.catalogo.importar.colDescripcion')}</li>
        </ol>
        <p className="text-xs text-hce-muted">{t('farmaciaApp.catalogo.importar.encabezado')}</p>
        <div className="pt-1">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-hce-ink hover:bg-hce-paper">
            {importando ? t('farmaciaApp.catalogo.importar.importando') : t('farmaciaApp.catalogo.importar.elegirArchivo')}
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" disabled={importando} aria-label={t('farmaciaApp.catalogo.importar.elegirArchivo')} onChange={(e) => void importar(e)} />
          </label>
        </div>
        {resultadoImportacion && (
          <div className="pt-1 text-xs">
            <p className="text-hce-ink">{t('farmaciaApp.catalogo.importar.resumen', { creados: resultadoImportacion.creados, fallidos: resultadoImportacion.fallidos.length })}</p>
            {resultadoImportacion.fallidos.length > 0 && (
              <ul className="mt-1 list-inside list-disc text-hce-muted">
                {resultadoImportacion.fallidos.map((f) => (
                  <li key={f.fila}>{t('farmaciaApp.catalogo.importar.filaError', { fila: f.fila, detalle: f.detalle })}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {items === undefined ? (
        <p className="text-sm text-hce-muted">{t('farmaciaApp.cargando')}</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-hce-muted">{t('farmaciaApp.catalogo.vacio')}</p>
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border">
          {items.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
              <span className="min-w-0 flex-1">
                <span className={`block truncate ${item.active ? 'text-hce-ink' : 'text-hce-muted line-through'}`}>{item.name}</span>
                <span className="block text-xs text-hce-muted">
                  {item.code} · {item.kind === 'drug' ? t('farmaciaApp.catalogo.tipoFarmaco') : t('farmaciaApp.catalogo.tipoInsumo')}
                  {!item.active && ` · ${t('farmaciaApp.catalogo.inactivo')}`}
                </span>
              </span>
              {editandoId === item.id ? (
                <span className="flex shrink-0 items-center gap-1">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    id="catalogo-precio-editado"
                    className="h-8 w-24"
                    value={precioEditado}
                    onChange={(e) => setPrecioEditado(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void guardarPrecio(item.id);
                      if (e.key === 'Escape') setEditandoId(undefined);
                    }}
                  />
                  <Button size="sm" className="h-8" disabled={guardandoEdicion} onClick={() => void guardarPrecio(item.id)}>
                    {t('farmaciaApp.catalogo.guardarPrecio')}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditandoId(undefined)}>
                    {t('farmaciaApp.catalogo.cancelar')}
                  </Button>
                </span>
              ) : (
                <span className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    className="font-mono text-xs text-hce-ink underline decoration-dotted underline-offset-2 hover:opacity-70"
                    title={t('farmaciaApp.catalogo.editarPrecio')}
                    onClick={() => empezarEdicionPrecio(item)}
                  >
                    {formatoUsd(item.amountCents)}
                  </button>
                  <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => void alternarActivo(item)}>
                    {item.active ? t('farmaciaApp.catalogo.desactivar') : t('farmaciaApp.catalogo.activar')}
                  </Button>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
